import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { useEffect, useState, useRef, useCallback } from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { PlayContainer } from 'Components/RoadmapComponents/PlayContainer';
import { Minimap } from 'Components/RoadmapComponents/Minimap';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { Path } from 'Components/RoadmapComponents/Path';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainAppReducer, UserLevelData } from 'Types';
import { getHighestCompletedLevel, getUnitPctComplete } from 'Utils/UserProgress';
import { CustomCircularProgress } from 'Components/StyledComponents';
import Survey from './Survey';
import './Roadmap.css';
import useMediaQuery from '@mui/material/useMediaQuery';
import { isCapacitorNative } from 'Utils/Constants';
import Confirmation from 'Containers/Payment/Confirmation';
import logger from 'Utils/Logger';

const smallMobileRoadmapQuery = '(max-width:480px), (max-height:480px)';

const Roadmap = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { levelData, levelSelect, userLevelData, tiersByLevels, unitSelect, userData } = data;

  // IMPORTANT: sane defaults (these are expected to be 0..1)
  const [areaToPreventScroll, setAreaToPreventScroll] = useState<string>('');
  const [scrollPos, setScrollPos] = useState<number>(0);
  const [scrollAreaSize, setScrollAreaSize] = useState<number>(0.5);

  const [pathScrollPos, setPathScrollPos] = useState(0);
  const roadmapRef = useRef<HTMLDivElement | null>(null);
  const latestLevelRef = useRef<HTMLDivElement | null>(null);

  const [cursorInRoadmap, setCursorInRoadmap] = useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get('show-thank-you');
    logger.info('queryParam:', queryParam);
    if (queryParam) setShowPaymentConfirmation(true);
  }, [location.search]);

  useEffect(() => {
    if (userData && userData.subscription_status == '') {
      navigate('/welcome');
    }
  }, [userData, navigate]);

  const [highestCompleteData, setHighestCompleteData] = useState(
    getHighestCompletedLevel(userLevelData),
  );
  useEffect(() => {
    setHighestCompleteData(getHighestCompletedLevel(userLevelData));
  }, [userLevelData]);

  const isSmallMobile = useMediaQuery(smallMobileRoadmapQuery);

  // Desktop-only: prevent trackpad/back-gesture weirdness. Skip on Capacitor.
  useEffect(() => {
    if (isCapacitorNative()) return;

    const preventNavigation = (e: WheelEvent) => {
      if (roadmapRef.current && !cursorInRoadmap) e.preventDefault();
    };

    const el = roadmapRef.current;
    if (el) {
      el.addEventListener('wheel', preventNavigation as EventListener, { passive: false });
      return () => el.removeEventListener('wheel', preventNavigation as EventListener);
    }
  }, [cursorInRoadmap]);

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!latestLevelRef.current) return;
    const boxRect = latestLevelRef.current.getBoundingClientRect();
    const isInside =
      e.clientX >= boxRect.left &&
      e.clientX <= boxRect.right &&
      e.clientY >= boxRect.top &&
      e.clientY <= boxRect.bottom;

    setCursorInRoadmap(isInside);
  };

  const handleUnitSelect = useCallback(
    (unit: number) => {
      dispatch(appActions.setUnitSelect({ unitSelect: unit }));
    },
    [dispatch],
  );

  const heightMatchPlayContainer = useMediaQuery('(min-height:600px)');

  const selectedLevel = levelData[levelSelect] || levelData[0];
  const level_number = selectedLevel ? selectedLevel.level_number : 0;
  const name = selectedLevel ? selectedLevel.name : '';
  const description = selectedLevel?.description;

  const lastLevelComplete = highestCompleteData.lastLevelComplete;
  const userLevelStatuses = highestCompleteData.userLevelStatuses;
  const userLevelTiersComplete = highestCompleteData.userLevelTiersComplete;

  let selectedLevelProgress: UserLevelData | undefined;
  userLevelData.forEach((ul) => {
    if (ul?.level?.level_number == level_number) selectedLevelProgress = ul;
  });

  const unitPercentComplete = getUnitPctComplete(lastLevelComplete, levelData);
  const tierByLevel = tiersByLevels
    ? tiersByLevels.find((item) => item.level_number === level_number)
    : undefined;
  const tierCountForLevel = tierByLevel?.tiers?.length || 1;

  if (!levelData || levelData.length < 2) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CustomCircularProgress />
        </Box>
      </Box>
    );
  }

  const isCap = isCapacitorNative();

  return (
    <Box
      sx={{ display: 'flex', height: '100vh', width: '100%' }}
      ref={roadmapRef}
      onPointerMove={handlePointerMove}
    >
      <Menu selected={'Roadmap'} />
      <Survey />

      {showPaymentConfirmation && <Confirmation setShowConfirmation={setShowPaymentConfirmation} />}

      <Box
        sx={{
          width: 'calc(100% - var(--app-sidebar-width, 100px))',
          backgroundColor: 'black',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'linear-gradient(0deg, black, #1D2F44)',
            height: '100%',
          }}
        >
          <Box className="header-container">
            <Box
              className="roadmap-title-container"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              Roadmap
              {!isCap &&
                (process.env.REACT_APP_NODE_ENV === 'local' ||
                  process.env.REACT_APP_NODE_ENV === 'dev') && (
                  <FormControl
                    size="small"
                    sx={{ color: '#fff', svg: { fill: '#fff' }, minWidth: 80 }}
                  >
                    <InputLabel id="unit-select-label" sx={{ color: '#fff' }}>
                      Unit
                    </InputLabel>
                    <Select
                      labelId="unit-select-label"
                      id="unit-select"
                      value={unitSelect.toString()}
                      label="Unit"
                      onChange={(evt: SelectChangeEvent) =>
                        handleUnitSelect(parseInt(evt.target.value))
                      }
                      sx={{ color: '#fff', svg: { fill: '#fff' } }}
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </FormControl>
                )}
            </Box>
            <Box className="minimap-container">
              <Minimap
                unitPercentComplete={unitPercentComplete}
                scrollPosFromRoadmap={scrollPos}
                setScrollPosFromRoadmap={setScrollPos}
                scrollAreaSize={scrollAreaSize}
                areaToPreventScroll={areaToPreventScroll}
                setAreaToPreventScroll={setAreaToPreventScroll}
                setPathScrollPos={setPathScrollPos}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box className="roadmap-container-styles">
              <Path
                levelSelect={levelSelect}
                unitSelect={unitSelect}
                levelData={levelData}
                userLevelData={userLevelData}
                lastLevelComplete={lastLevelComplete}
                userLevelStatuses={userLevelStatuses}
                userLevelTiersComplete={userLevelTiersComplete}
                scrollPos={scrollPos}
                setScrollPos={setScrollPos}
                setScrollAreaSize={setScrollAreaSize}
                areaToPreventScroll={areaToPreventScroll}
                setAreaToPreventScroll={setAreaToPreventScroll}
                pathScrollPos={pathScrollPos}
                latestLevelRef={latestLevelRef}
                isSmallMobile={isSmallMobile}
              />
            </Box>

            {!isSmallMobile && (!isCap || heightMatchPlayContainer) && (
              <PlayContainer
                selectedLevel={selectedLevel}
                levelSelect={levelSelect}
                level_number={level_number}
                name={name}
                description={description}
                selectedLevelProgress={selectedLevelProgress}
                tierCountForLevel={tierCountForLevel}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Roadmap;
